import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const useCases = [
  {
    title: "Developers",
    iconSrc: "/os/assets/icons/code-editor.svg",
    iconAlt: "Code Editor",
    summary: "Build, debug, and ship on mapleos.",
    bullets: [
      "A familar IDE interface based on VS Code.",
      "Build any kind of app, from games to utilities using our WASM runtimes.",
      "Starter projects to help you get started.",
      "Work with uninterrupted focus. No Slack support - it's a feature.",
    ],
  },
  {
    title: "System Builders",
    iconSrc: "/os/assets/icons/ai-center.svg",
    iconAlt: "Workflow tools",
    summary: "Compose workflows where tools, execution, and results live in the same system.",
    bullets: [
      "Prototype multi-step workflows with Blueprints instead of brittle glue code.",
      "Combine local compute and cloud services from one interface.",
      "Keep control over prompts, modules, and outputs across your workflow.",
    ],
  },
  {
    title: "Researchers",
    iconSrc: "/os/assets/icons/maple-seed-vault.svg",
    iconAlt: "Maple Seed Vault",
    summary: "Gather evidence, compare outputs, and preserve reasoning context over time.",
    bullets: [
      "Bring notes, sources, files, and assistance into mapleos.",
      "Trace how answers were formed instead of relying on isolated chat history.",
      "Take advantage of our privacy-first architecture for critical research topics.",
    ],
  },
  {
    title: "Creators",
    iconSrc: "/os/assets/icons/paint-program.svg",
    iconAlt: "Paint Program",
    summary: "Create with a system that celebrates human creativity.",
    bullets: [
      "Keep writing, media, references, and ideation safe in mapleos.",
      "Switch between rough concepts and structured production without context loss.",
      "Create, think, and create some more — without algorithmic noise taking over your flow.",
    ],
  },
];

export default function UseCases() {
  return (
    <section id="built-for-builders" className="scroll-mt-24 px-6 py-20 md:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55 }}
          className="mx-auto mb-12 max-w-3xl text-center"
        >
          <h2 className="mt-5 text-3xl font-semibold tracking-[-0.05em] text-white md:text-5xl">
            Built for builders.
          </h2>

          <p className="mt-4 text-base leading-7 text-white/60 md:text-lg">
            From coding to research to creative work, mapleos keeps your tools, files, and context in one place.
          </p>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {useCases.map((useCase, index) => {
            return (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
              >
                <Card className="h-full rounded-3xl border-white/10 bg-white/5 text-white shadow-[0_18px_50px_rgba(0,0,0,0.18)] backdrop-blur">
                  <CardHeader className="space-y-5">
                    <img
                      src={useCase.iconSrc}
                      alt={useCase.iconAlt}
                      className="h-16 w-16 object-contain md:h-[4.5rem] md:w-[4.5rem]"
                      loading="lazy"
                    />
                    <div>
                      <CardTitle className="text-2xl tracking-[-0.04em] text-white">{useCase.title}</CardTitle>
                      <p className="mt-3 text-sm leading-7 text-white/60">{useCase.summary}</p>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-sm leading-7 text-white/70">
                      {useCase.bullets.map((bullet) => (
                        <li key={bullet} className="flex gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}